/**
 * 华奋达磁轴 vs 机械轴区分。
 * Battle68（BAT68）为华奋达方案第一款磁轴键盘；既有 X87 / SMART875 / M98 等为机械轴。
 * 磁轴走行程/RT/DKS/校准等专用协议，不可与机械轴菜单与写路径混用。
 */

export type HfdMagneticAxisEquipmentLike = {
  solutionRovider?: unknown;
  keyboardType?: unknown;
  displayname?: unknown;
  productName?: unknown;
  identifier?: unknown;
  magneticAxis?: unknown;
};

/** 已确认的华奋达磁轴型号（keyboardType / 名称） */
const HFD_MAGNETIC_KEYBOARD_TYPES = new Set(["huafenda68", "tank68he"]);

function equipmentNameBlob(
  equipment: HfdMagneticAxisEquipmentLike | null | undefined
): string {
  if (!equipment) return "";
  return [
    equipment.displayname,
    equipment.productName,
    equipment.identifier,
    equipment.keyboardType,
  ]
    .map((v) => String(v ?? "").trim())
    .filter(Boolean)
    .join(" ")
    .toUpperCase();
}

/** 是否华奋达磁轴设备（设备表 magneticAxis，或 battle68 / BAT68 / huafenda68） */
export function isHuafendaMagneticAxisEquipment(
  equipment: HfdMagneticAxisEquipmentLike | null | undefined
): boolean {
  if (!equipment || typeof equipment !== "object") return false;
  if (String(equipment.solutionRovider ?? "") !== "huafenda") return false;
  if (equipment.magneticAxis === true) return true;

  const kt = String(equipment.keyboardType ?? "").trim();
  if (HFD_MAGNETIC_KEYBOARD_TYPES.has(kt)) return true;

  const blob = equipmentNameBlob(equipment);
  return (
    blob.includes("BATTLE68") ||
    blob.includes("BAT68") ||
    blob.includes("TANK68") ||
    blob.includes("HUAFENDA68")
  );
}

/** 华奋达机械轴（同方案下非磁轴） */
export function isHuafendaMechanicalAxisEquipment(
  equipment: HfdMagneticAxisEquipmentLike | null | undefined
): boolean {
  if (!equipment || typeof equipment !== "object") return false;
  if (String(equipment.solutionRovider ?? "") !== "huafenda") return false;
  return !isHuafendaMagneticAxisEquipment(equipment);
}

/** Tank68 HE 磁轴行程范围（mm） */
export const TANK68HE_TRAVEL_MIN_MM = 0.1;
export const TANK68HE_TRAVEL_MAX_MM = 3.3;

/** 其它华奋达磁轴默认行程范围（mm） */
export const HFD_MAGNETIC_TRAVEL_MIN_MM = 0.1;
export const HFD_MAGNETIC_TRAVEL_MAX_MM = 3.5;

export function isTank68HeKeyboardType(keyboardType?: string | null): boolean {
  return String(keyboardType ?? "").trim() === "tank68he";
}

/** 按型号返回华奋达磁轴 UI / 固件行程上下限（mm） */
export function resolveHfdMagneticTravelRangeMm(keyboardType?: string | null): {
  min: number;
  max: number;
} {
  if (isTank68HeKeyboardType(keyboardType)) {
    return { min: TANK68HE_TRAVEL_MIN_MM, max: TANK68HE_TRAVEL_MAX_MM };
  }
  return { min: HFD_MAGNETIC_TRAVEL_MIN_MM, max: HFD_MAGNETIC_TRAVEL_MAX_MM };
}

/** Tank68 HE 校准 route 单位为 µm（÷1000 得 mm）；>100 约 0.1mm 视为按压中 */
export const TANK68HE_CALIBRATION_ROUTE_ACTIVE_UM = 100;

/** Tank68 HE 快速校准：是否达到「按压中」行程（勿用通用 3000µm 阈值，满行程仅 3.3mm） */
export function isTank68HeCalibrationRouteActive(route: unknown): boolean {
  const n = Number(route);
  return Number.isFinite(n) && n > TANK68HE_CALIBRATION_ROUTE_ACTIVE_UM;
}
